import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { getUserAvatarUrl, handleUserAvatarError } from '../utils/avatars';
import { getCountryFlag } from '../utils/countries';
import { formatTimeFromDate, formatNumericDate } from '../utils/timeFormat';
import './RouteTimesModal.css';

// Выбор участника «скрывать гонки без регистрации» запоминаем между открытиями модалки
const HIDE_UNREGISTERED_KEY = 'route_times_hide_unregistered';

// Стартовый лист на бэкенде кешируется на 5 минут — столько же держим его на клиенте,
// чтобы повторное раскрытие плашки не дергало API впустую
const START_LIST_TTL_MS = 5 * 60 * 1000;

// Стартовый лист временно скрыт: для официальных событий ROUVY не отдает его по токену,
// а парсинг страниц riders.rouvy.com еще не готов. Вернуть флаг в true, когда заработает
const START_LIST_ENABLED = false;

// Из «Spanish Grand Tour | Racing Series | Stage 4 | Race G» в плашке нужен только «Race G»:
// событие и этап и так видны в заголовке
const extractRaceLabel = (name) => {
  if (!name) return null;
  const match = name.match(/(?:^|[|\-–—/])\s*(Race\s+\S+)\s*$/i);
  return match ? match[1] : null;
};

const localDayKey = (date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

// Строки таблицы — полные недели Пн–Вс, в которые попадают гонки, в т.ч. дни без гонок
const groupTimesByDay = (times) => {
  const dated = times
    .map(time => ({ time, date: new Date(time.start_date_time_utc) }))
    .filter(({ date }) => !Number.isNaN(date.getTime()))
    .sort((a, b) => a.date - b.date);
  if (dated.length === 0) return [];

  const byDay = new Map();
  dated.forEach(({ time, date }) => {
    const key = localDayKey(date);
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key).push({ time, date });
  });

  // getDay(): 0 — воскресенье, поэтому сдвиг до понедельника (d + 6) % 7, до воскресенья (7 - d) % 7
  const first = dated[0].date;
  const last = dated[dated.length - 1].date;
  const lastKey = localDayKey(
    new Date(last.getFullYear(), last.getMonth(), last.getDate() + (7 - last.getDay()) % 7)
  );
  const rows = [];
  // Шагаем через конструктор, а не +24ч, чтобы переход на летнее время не сбивал дни
  for (let day = new Date(first.getFullYear(), first.getMonth(), first.getDate() - (first.getDay() + 6) % 7); ;
       day = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1)) {
    const key = localDayKey(day);
    rows.push({ key, date: day, slots: byDay.get(key) || [] });
    if (key === lastKey) break;
  }
  return rows;
};

const RouteTimesModal = ({
  isOpen,
  onClose,
  eventId,
  routeName,
  times,
  userActivities = [],
  wildCard = null,
  // Лимит гонок на этапе (events.races_per_stage) и состояние этапа участника
  racesPerStage = null,
  stage = null,
  canRegister = false,
  onRegister,
  onUnregister,
  // Дополнительные заезды пользователя для этого маршрута (RMC)
  userExtraRaces = []
}) => {
  const { t, language, pluralize } = useLanguage();

  // Склонение слова «гонка» по числу
  const racesWord = (count) => pluralize(
    count,
    t('route_card.race_limit.race_one'),
    t('route_card.race_limit.race_few'),
    t('route_card.race_limit.race_many')
  );

  // Гонка, для которой открыт диалог регистрации
  const [pendingRace, setPendingRace] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [hideUnregistered, setHideUnregistered] = useState(
    () => localStorage.getItem(HIDE_UNREGISTERED_KEY) === 'true'
  );

  // Стартовые листы гонок: { [eventRouteId]: { loading, error, data, fetchedAt } }
  const [startLists, setStartLists] = useState({});
  // Гонка, для которой открыт стартовый лист
  const [startListRace, setStartListRace] = useState(null);

  // Модалка переиспользуется для разных маршрутов — открытый список не должен переезжать
  useEffect(() => {
    setStartListRace(null);
  }, [isOpen, routeName]);

  const handleToggleHideUnregistered = (e) => {
    const { checked } = e.target;
    setHideUnregistered(checked);
    localStorage.setItem(HIDE_UNREGISTERED_KEY, String(checked));
  };

  const fetchStartList = async (time) => {
    const current = startLists[time.id];
    if (current?.loading) return;
    // Данные еще свежие — обходимся кешем
    if (current?.data && Date.now() - current.fetchedAt < START_LIST_TTL_MS) return;

    setStartLists(prev => ({ ...prev, [time.id]: { ...prev[time.id], loading: true, error: null } }));

    try {
      const response = await axios.get(`/api/events/${eventId}/event_routes/${time.id}/start_list`);
      setStartLists(prev => ({
        ...prev,
        [time.id]: { loading: false, error: null, data: response.data, fetchedAt: Date.now() }
      }));
    } catch (err) {
      setStartLists(prev => ({
        ...prev,
        [time.id]: {
          loading: false,
          error: err?.response?.data?.error || t('route_card.start_list.load_failed'),
          data: null
        }
      }));
    }
  };

  const handleOpenStartList = (e, time) => {
    e.preventDefault();
    e.stopPropagation();

    setStartListRace(time);
    fetchStartList(time);
  };

  const participantName = (participant) => {
    const fullName = [participant.first_name, participant.last_name].filter(Boolean).join(' ');
    return fullName || participant.username;
  };

  // Проверяем, завершил ли пользователь событие
  const isEventCompleted = (rouvyEventId) => {
    return userActivities.some(
      activity => activity.event_id === rouvyEventId && activity.finish_time !== null
    );
  };

  // Получаем ID события с лучшим временем среди завершенных
  const getBestTimeEventId = () => {
    const completedActivities = userActivities.filter(
      activity => activity.finish_time !== null
    );

    if (completedActivities.length === 0) return null;

    // Находим активность с минимальным finish_time
    const bestActivity = completedActivities.reduce((best, current) => {
      return current.finish_time < best.finish_time ? current : best;
    });

    return bestActivity.event_id;
  };

  const bestTimeEventId = getBestTimeEventId();

  // Ограничение на количество гонок этапа
  const limit = racesPerStage ?? stage?.limit ?? null;
  const limited = Boolean(limit);
  const registeredCount = stage?.registered_race_ids?.length ?? times.filter(time => time.registered).length;
  const completedCount = stage?.completed_count ?? times.filter(time => time.completed).length;
  // Все допустимые попытки проеханы — этап закрыт
  const stageLocked = limited && (stage?.locked ?? completedCount >= limit);
  const slotsLeft = limited ? Math.max(limit - registeredCount, 0) : null;

  if (!isOpen) return null;

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'offline':
        return 'status-offline';
      case 'finished':
        return 'status-finished';
      case 'active':
        return 'status-active';
      default:
        return 'status-default';
    }
  };

  const getStatusText = (status) => {
    return t(`route_card.status.${status}`) || status;
  };

  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return '';
    try {
      const date = new Date(dateTimeStr);

      const time = formatTimeFromDate(date, language);

      const dayOfWeekIndex = date.getDay();
      const dayOfWeek = t('common.days')[dayOfWeekIndex];
      const isWeekend = dayOfWeekIndex === 0 || dayOfWeekIndex === 6;

      // Get timezone abbreviation
      const timeZone = date.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop();

      const daySpan = isWeekend ? `<span class="weekend-day">${dayOfWeek}</span>` : dayOfWeek;

      return `${daySpan}, ${formatNumericDate(date, language)}, ${time} ${timeZone}`;
    } catch (e) {
      return dateTimeStr;
    }
  };

  const handleViewRoute = (rouvyEventId) => {
    const url = `https://riders.rouvy.com/events/${rouvyEventId}`;
    window.open(url, '_blank');
  };

  const isUpcoming = (startDateTime) => {
    return new Date(startDateTime) > new Date();
  };

  const isRegistered = (time) => {
    if (time.registered !== undefined) return Boolean(time.registered);
    return Boolean(stage?.registered_race_ids?.includes(time.id));
  };

  // Гонку еще можно проехать и есть свободная регистрация на этапе
  const isRegistrable = (time) => {
    return limited && canRegister && !stageLocked && slotsLeft > 0
      && !isRegistered(time) && time.status !== 'finished';
  };

  // Лимит регистраций выбран: будущие гонки, на которые уже не записаться
  const isUnavailable = (time) => {
    return limited && !stageLocked && slotsLeft === 0
      && !isRegistered(time) && isUpcoming(time.start_date_time_utc);
  };

  // Прятать лишние гонки имеет смысл, когда все регистрации этапа уже распределены
  const canHideUnregistered = limited && slotsLeft === 0;

  // Преобразуем extra races в формат time для отображения в расписании
  const extraRaceTimes = userExtraRaces.map(extraRace => ({
    id: `extra-${extraRace.id}`,
    rouvy_event_id: extraRace.event_link.match(/events\/([a-f0-9-]{36})/i)?.[1] || null,
    start_date_time_utc: extraRace.start_date_time_utc,
    status: 'active',
    name: null,
    accessibility: null,
    drafting: null,
    registered: false,
    completed: false,
    is_extra_race: true,
    extra_race_link: extraRace.event_link
  })).filter(time => time.start_date_time_utc); // Только extra races с известной датой старта

  // Объединяем официальные гонки и extra races
  const allTimes = [...times, ...extraRaceTimes];

  const visibleTimes = canHideUnregistered && hideUnregistered
    ? allTimes.filter(time => isRegistered(time))
    : allTimes;
  // Диапазон дней строим по всем гонкам: фильтр не должен перекраивать таблицу
  const visibleIds = new Set(visibleTimes.map(time => time.id));
  const scheduleRows = groupTimesByDay(allTimes).map(row => ({
    ...row,
    slots: row.slots.filter(({ time }) => visibleIds.has(time.id))
  }));
  const todayKey = localDayKey(new Date());
  const timeZoneAbbr = scheduleRows.length > 0
    ? scheduleRows[0].date.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop()
    : '';

  const handleSlotClick = (time) => {
    // Extra race — открываем напрямую по ссылке
    if (time.is_extra_race) {
      window.open(time.extra_race_link, '_blank');
      return;
    }

    if (isRegistrable(time)) {
      setError(null);
      setPendingRace(time);
      return;
    }

    handleViewRoute(time.rouvy_event_id);
  };

  const handleConfirmRegistration = async () => {
    if (!pendingRace || submitting) return;

    setSubmitting(true);
    setError(null);

    try {
      await onRegister(pendingRace.id);
      const rouvyEventId = pendingRace.rouvy_event_id;
      setPendingRace(null);
      handleViewRoute(rouvyEventId);
    } catch (err) {
      setError(err?.response?.data?.error || t('route_card.race_limit.register_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnregister = async (e, time) => {
    e.preventDefault();
    e.stopPropagation();
    if (submitting) return;

    setSubmitting(true);
    setError(null);

    try {
      await onUnregister(time.id);
    } catch (err) {
      setError(err?.response?.data?.error || t('route_card.race_limit.unregister_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderSlot = (time, date) => {
    const registered = limited && isRegistered(time);
    const isExtraRace = time.is_extra_race;

    // Этап выкатан — все гонки серые; лимит регистраций выбран — оставшиеся бледно-синие
    let badgeClass = getStatusBadgeClass(time.status);
    if (isExtraRace) {
      badgeClass = 'time-slot-badge-extra-race';
    } else if (stageLocked) {
      badgeClass = 'time-slot-badge-locked';
    } else if (isUnavailable(time)) {
      badgeClass = 'time-slot-badge-unavailable';
    }

    const startList = startLists[time.id];
    const raceLabel = isExtraRace ? '🏁' : extractRaceLabel(time.name);

    return (
      <div key={time.id} className="time-slot-badge-wrapper">
        {/* Подсказка о регистрации — во всплывающей подсказке, чтобы не раздувать плашку:
            клик все равно открывает диалог подтверждения */}
        <button
          onClick={() => handleSlotClick(time)}
          className={`time-slot-badge ${badgeClass} ${registered ? 'time-slot-badge-registered' : ''}`}
          title={isExtraRace ? t('route_card.extra_race.label') : (isRegistrable(time) ? t('route_card.race_limit.register_hint') : undefined)}
        >
          <span className="badge-main">
            <span className="badge-time">{formatTimeFromDate(date, language)}</span>
            {raceLabel && <span className="badge-race">{raceLabel}</span>}
          </span>
          {registered && (
            <span className="badge-registered-label">
              {t('route_card.race_limit.registered')}
            </span>
          )}
        </button>
        {registered && !isExtraRace && (
          <button
            className="badge-unregister-button"
            onClick={(e) => handleUnregister(e, time)}
            disabled={submitting}
            title={t('route_card.race_limit.unregister')}
            aria-label={t('route_card.race_limit.unregister')}
          >
            ×
          </button>
        )}

        {/* Стартовый лист открывается отдельным окном: в плашку список не влезает */}
        {START_LIST_ENABLED && !isExtraRace && (
          <button
            className="start-list-button"
            onClick={(e) => handleOpenStartList(e, time)}
            title={t('route_card.start_list.title')}
            aria-label={t('route_card.start_list.title')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            {startList?.data && (
              <span className="start-list-button-count">{startList.data.total}</span>
            )}
          </button>
        )}

        {!isExtraRace && isEventCompleted(time.rouvy_event_id) && (
          <div className="completed-badge" title={t('route_card.completed_by_participant')}>
            <span className="checkmark">✓</span>
          </div>
        )}
        {!isExtraRace && bestTimeEventId === time.rouvy_event_id && (
          <div className="best-time-badge" title={t('route_card.best_time')}>
            <span className="trophy-icon">⭐</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {t('route_card.schedule_title')}: {routeName}
          </h2>
          <button className="modal-close" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {canHideUnregistered && (
          <div className="modal-subheader">
            <label className="schedule-filter">
              <input
                type="checkbox"
                checked={hideUnregistered}
                onChange={handleToggleHideUnregistered}
              />
              <span>{t('route_card.race_limit.hide_unregistered')}</span>
            </label>
          </div>
        )}

        <div className="modal-body">
          {limited && (
            <div className={`race-limit-notice ${stageLocked ? 'race-limit-notice-locked' : ''}`}>
              <div className="race-limit-notice-title">
                ⚠️ {t('route_card.race_limit.warning')
                  .replace('{{count}}', limit)
                  .replace('{{races}}', racesWord(limit))}
              </div>
              {stageLocked ? (
                <div className="race-limit-notice-text">
                  {t('route_card.race_limit.stage_locked')}
                </div>
              ) : (
                <div className="race-limit-notice-text">
                  {t('route_card.race_limit.registered_count')
                    .replace('{{registered}}', registeredCount)
                    .replace('{{count}}', limit)}
                  {canRegister
                    ? ` · ${t('route_card.race_limit.slots_left').replace('{{count}}', slotsLeft)}`
                    : ` · ${t('route_card.race_limit.event_registration_required')}`}
                </div>
              )}
            </div>
          )}

          {error && <div className="race-limit-error">{error}</div>}

          {wildCard && (
            <div className="wildcard-schedule-section">
              <div className="wildcard-schedule-label">
                🃏 {t('route_card.wild_card.schedule_label')}
              </div>
              <div className={`wildcard-schedule-badge wildcard-schedule-${wildCard.status}`}>
                {wildCard.status === 'approved' && wildCard.event_link ? (
                  <a
                    href={`${wildCard.event_link}/leaderboard`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="wildcard-schedule-link"
                  >
                    {wildCard.ride_time && (
                      <span dangerouslySetInnerHTML={{ __html: formatDateTime(wildCard.ride_time) }} />
                    )}
                  </a>
                ) : !wildCard.ride_time ? (
                  <span>{t(`route_card.wild_card.schedule_status.${wildCard.status}`)}</span>
                ) : null}
              </div>
            </div>
          )}

          <table className="schedule-table">
            {timeZoneAbbr && <caption className="schedule-table-tz">{timeZoneAbbr}</caption>}
            <tbody>
              {scheduleRows.map(row => {
                const dayOfWeekIndex = row.date.getDay();
                const isWeekend = dayOfWeekIndex === 0 || dayOfWeekIndex === 6;
                const isToday = row.key === todayKey;
                const rowClass = [
                  isWeekend && 'schedule-row-weekend',
                  isToday && 'schedule-row-today'
                ].filter(Boolean).join(' ');

                return (
                  <tr key={row.key} className={rowClass} aria-current={isToday ? 'date' : undefined}>
                    <th scope="row" className="schedule-day">
                      <span className="schedule-day-name">{t('common.days')[dayOfWeekIndex]}</span>
                      <span className="schedule-day-date">{formatNumericDate(row.date, language)}</span>
                    </th>
                    <td className="schedule-slots">
                      {row.slots.length > 0 && (
                        <div className="time-slots-row">
                          {row.slots.map(({ time, date }) => renderSlot(time, date))}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {startListRace && (
        <div
          className="start-list-overlay"
          onClick={(e) => { e.stopPropagation(); setStartListRace(null); }}
        >
          <div className="start-list-modal" onClick={(e) => e.stopPropagation()}>
            <div className="start-list-modal-header">
              <div className="start-list-modal-titles">
                <h3 className="start-list-modal-title">{t('route_card.start_list.title')}</h3>
                <div
                  className="start-list-modal-race"
                  dangerouslySetInnerHTML={{ __html: formatDateTime(startListRace.start_date_time_utc) }}
                />
              </div>
              <button className="modal-close" onClick={() => setStartListRace(null)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {(() => {
              const startList = startLists[startListRace.id];

              if (startList?.loading) {
                return <div className="start-list-status">{t('route_card.start_list.loading')}</div>;
              }
              if (startList?.error) {
                return (
                  <div className="start-list-status start-list-status-error">{startList.error}</div>
                );
              }
              if (!startList?.data || startList.data.total === 0) {
                return <div className="start-list-status">{t('route_card.start_list.empty')}</div>;
              }

              return (
                <>
                  <div className="start-list-summary">
                    {t('route_card.start_list.total')}: <strong>{startList.data.total}</strong>
                    {startList.data.platform_total > 0 && (
                      <span className="start-list-summary-platform">
                        {' · '}{t('route_card.start_list.ours')}: <strong>{startList.data.platform_total}</strong>
                      </span>
                    )}
                  </div>
                  <ul className="start-list-items">
                    {startList.data.participants.map((participant, index) => (
                      <li
                        key={participant.user_id || `${participant.username}-${index}`}
                        className={`start-list-item ${participant.platform_user ? 'start-list-item-platform' : ''}`}
                      >
                        <img
                          className="start-list-avatar"
                          src={getUserAvatarUrl(participant.platform_user?.avatar || participant.avatar_url)}
                          alt={participant.username || ''}
                          onError={handleUserAvatarError}
                        />
                        <span className="start-list-name">
                          <span className="start-list-name-main">
                            {getCountryFlag(participant.country_code) && (
                              <span className="start-list-flag">
                                {getCountryFlag(participant.country_code)}
                              </span>
                            )}
                            {participantName(participant)}
                          </span>
                          {/* У участника платформы — его команда в этом событии (или никакой),
                              у остальных — команда из профиля ROUVY */}
                          {(participant.platform_user ? participant.platform_user.team : participant.team) && (
                            <span className="start-list-team">
                              {participant.platform_user ? participant.platform_user.team : participant.team}
                            </span>
                          )}
                        </span>
                        {participant.platform_user && (
                          <span
                            className="start-list-platform-badge"
                            title={t('route_card.start_list.on_platform_hint')}
                          >
                            {t('route_card.start_list.on_platform')}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {pendingRace && (
        <div className="race-confirm-overlay" onClick={(e) => { e.stopPropagation(); setPendingRace(null); }}>
          <div className="race-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="race-confirm-title">{t('route_card.race_limit.confirm_title')}</h3>
            <p className="race-confirm-text">{t('route_card.race_limit.confirm_text')}</p>
            <div
              className="race-confirm-race"
              dangerouslySetInnerHTML={{ __html: formatDateTime(pendingRace.start_date_time_utc) }}
            />
            <p className="race-confirm-hint">
              {t('route_card.race_limit.confirm_hint')
                .replace('{{count}}', limit)
                .replace('{{races}}', racesWord(limit))
                .replace('{{left}}', slotsLeft)}
            </p>
            <div className="race-confirm-actions">
              {/* Лимит регистраций исчерпан — остается только переход в Rouvy */}
              {isRegistrable(pendingRace) && (
                <button
                  className="race-confirm-btn race-confirm-btn-primary"
                  onClick={handleConfirmRegistration}
                  disabled={submitting}
                >
                  {submitting ? t('common.saving') : t('route_card.race_limit.register_and_open')}
                </button>
              )}
              <button
                className="race-confirm-btn race-confirm-btn-secondary"
                onClick={() => {
                  const rouvyEventId = pendingRace.rouvy_event_id;
                  setPendingRace(null);
                  handleViewRoute(rouvyEventId);
                }}
                disabled={submitting}
              >
                {t('route_card.race_limit.open_only')}
              </button>
              <button
                className="race-confirm-btn race-confirm-btn-cancel"
                onClick={() => setPendingRace(null)}
                disabled={submitting}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteTimesModal;
